// services/consumerApi.ts

import {
  Communication,
  CommunicationFilters,
  CommunicationPayload,
} from "@features/cx/shared/types/communicationTypes";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  bxApiClient,
  communicationApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { DropdownItem } from "@shared/api/types";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { ServiceOrderFilters } from "../shared/types/serviceOrdersTabProps";
import {
  BillData,
  CategoryOption,
  Complaint,
  ComplaintFilters,
  Consumer,
  ConsumerDetailsResponse,
  ConsumerFilters,
  ConsumerSummary,
  ConsumptionData,
  CreateConsumerPayload,
  CreateConsumerResponse,
  Document,
  DocumentFilters,
  FinancialFilters,
  MeterReading,
  PaginatedResponse,
  PaymentChannel,
  PaymentMethod,
  PaymentPayType,
  PaymentRecord,
  Plan,
  Premise,
  RelationshipInsights,
  ServiceRequest,
  SubcategoryOption,
  UpdateConsumerPayload,
  UpdateConsumerResponse,
  UtilityConnection,
  consumerConnectinoHistory
} from "./types";
import { CreatePaymentPayload } from "../payments/types";

// API Functions
export const consumerApi = {
  // Consumer Profile Operations
  getConsumer: async (
    consumerNo: string,
    remoteUtilityId: string
  ): Promise<Consumer> => {
    const response = await cxApiClient.get(
      ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "consumer-detail",
        (params) => {
          params.push("consumer_no", consumerNo);
          params.push("remote_utility_id", remoteUtilityId.toString());
          return params;
        }
      )
    );
    const transformedResponse = transformJsonToObject<Consumer>(
      response.data.result
    );
    return transformedResponse;
  },

    getConsumerConnectionHistory: async (
    consumerID: string,
    remoteUtilityId: string
  ): Promise<consumerConnectinoHistory> => {
    const response = await cxApiClient.get(
      ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "meter-history",
        (params) => {
          params.push("consumer_id", consumerID);
          params.push("remote_utility_id", remoteUtilityId.toString());
          return params;
        }
      )
    );
    const transformedResponse = transformJsonToObject<Consumer>(
      response.data.result
    );
    return transformedResponse;
  },

  getConsumerBillData: async (
    consumerNo: string,
    remoteUtilityId: string
  ): Promise<Consumer> => {
    const response = await cxApiClient.get(
      ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "consumer-bill",
        (params) => {
          params.push("consumer_number", consumerNo);
          params.push("remote_utility_id", remoteUtilityId.toString());
          params.push("fetch_latest", "True");
          params.push("bill_data", "True");
          return params;
        }
      )
    );
    const transformedResponse = transformJsonToObject<Consumer>(
      response.data.result
    );
    return transformedResponse;
  },

  searchConsumers: async (
    searchTerm: string,
    remoteUtilityId: number
  ): Promise<{
    result: Array<{ value: string; id: string; status: string }>;
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "search-consumer",
      (params) => {
        params.push("search_field", "consumer_no");
        params.push("search_data", searchTerm);
        params.push("remote_utility_id", remoteUtilityId.toString());
        return params;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data.data;
  },

  getPaymentHistory: async (
    id: string,
    filters: FinancialFilters = {}
  ): Promise<PaginatedResponse<PaymentRecord>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment",
      (query) => {
        query.push("consumer_id", id);
        query.push("remote_utility_id", filters.remote_utility_id?.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
      }
    );

    const response = await cxApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<PaymentRecord>
    >(response.data);
    return transformedResponse;
  },

  getBillingHistory: async (
    id: string,
    filters: FinancialFilters = {}
  ): Promise<PaginatedResponse<BillData>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-bill",
      (query) => {
        query.push("consumer_id", id);
        query.push("remote_utility_id", filters.remote_utility_id?.toString());
        query.push("fetch_last_six_records", "True");
        query.push("bill_data", "True");
        query.push("is_pagination_required", "True");
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
      }
    );

    const response = await cxApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<BillData>
    >(response.data);
    return transformedResponse;
  },

  // Connections Tab Operations
  getUtilityConnections: async (
    id: string,
    remote_utility_id: string
  ): Promise<UtilityConnection[]> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-connections",
      [id],
      (params) => {
        params.push("remote_utility_id", remote_utility_id?.toString());
        return params;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data.data;
  },

  getMeterReadings: async (
    consumerId: string,
    meterId: string,
    filters: { page: number; limit: number },
    remote_utility_id: string | number
  ): Promise<PaginatedResponse<MeterReading>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "meter-reading",
      (params) => {
        params.push("remote_utility_id", remote_utility_id);
        params.push("consumer_id", consumerId);
        params.push("remote_meter_id", meterId);
        params.push("is_status", "false");
        if (filters.page) params.push("page", filters.page.toString());
        if (filters.limit) params.push("limit", filters.limit.toString());
        return params;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Service Tab Operations
  getServiceOrders: async (
    id: string,
    filters: ServiceOrderFilters = {}
  ): Promise<PaginatedResponse<Complaint>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-support-request",
      (query) => {
        query.push("consumer_id", id);
        query.push("request_type", "Service");
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.status?.length) {
          filters.status.forEach((status) => query.push("status", status));
        }
        if (filters.order_type) query.push("order_type", filters.order_type);
        if (filters.date_range?.start)
          query.push("start_date", filters.date_range.start);
        if (filters.date_range?.end)
          query.push("end_date", filters.date_range.end);
        if (filters.query) query.push("query", filters.query);
        if (filters.sort_field) query.push("sort_field", filters.sort_field);
        if (filters.sort_direction)
          query.push("sort_direction", filters.sort_direction);
        query.push("remote_utility_id", filters.remote_utility_id);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<Complaint>
    >(response.data);
    return transformedResponse;
  },

  // Complaints Tab Operations
  getComplaints: async (
    id: string,
    filters: ComplaintFilters = {}
  ): Promise<PaginatedResponse<Complaint>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-support-request",
      (query) => {
        query.push("consumer_id", id);
        query.push("request_type", "Complaint");
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.date_range?.start)
          query.push("start_date", filters.date_range.start);
        if (filters.date_range?.end)
          query.push("end_date", filters.date_range.end);
        if (filters.query) query.push("query", filters.query);
        if (filters.sort_field) query.push("sort_field", filters.sort_field);
        if (filters.sort_direction)
          query.push("sort_direction", filters.sort_direction);
        query.push("remote_utility_id", filters.remote_utility_id?.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<Complaint>
    >(response.data);
    return transformedResponse;
  },

  createComplaint: async (
    id: string,
    remote_utility_id: string,
    data: Omit<Complaint, "id" | "date" | "complaintNumber">
  ): Promise<Complaint> => {
    const payload = {
      ...data,
      consumer_id: id,
      request_type: "Complaint",
      remote_utility_id: remote_utility_id?.toString(),
    };

    const response = await cxApiClient.post(
      ApiEndpoints.createUrlWithPathVariables(
        "cx",
        "consumer-complaint-request"
      ),
      payload
    );
    return response.data;
  },

  // Service Requests Tab Operations
  getServiceRequests: async (
    id: string,
    filters: ServiceOrderFilters = {}
  ): Promise<PaginatedResponse<ServiceRequest>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-support-request",
      (query) => {
        query.push("consumer_id", id);
        query.push("active_tab", "Request");
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.date_range?.start)
          query.push("start_date", filters.date_range.start);
        if (filters.date_range?.end)
          query.push("end_date", filters.date_range.end);
        if (filters.query) query.push("query", filters.query);
        if (filters.sort_field) query.push("sort_field", filters.sort_field);
        if (filters.sort_direction)
          query.push("sort_direction", filters.sort_direction);
        query.push("remote_utility_id", filters.remote_utility_id?.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<ServiceRequest>
    >(response.data);
    return transformedResponse;
  },

  createServiceRequest: async (
    id: string,
    remoteUtilityId: string,
    data: Omit<ServiceRequest, "id" | "date">
  ): Promise<ServiceRequest> => {
    const payload = {
      ...data,
      consumer_id: id,
      remote_utility_id: remoteUtilityId,
    };

    const response = await cxApiClient.post(
      ApiEndpoints.createUrlWithPathVariables(
        "cx",
        "consumer-service-request",
        []
      ),
      payload
    );
    return response.data;
  },

  // Communication Tab Operations
  getCommunicationHistory: async (
    id: string,
    filters: CommunicationFilters = {}
  ): Promise<PaginatedResponse<Communication>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "notifications",
      (query) => {
        query.push("consumer_id", id);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.communicationType)
          query.push("type", filters.communicationType);
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
      }
    );

    const response = await communicationApiClient.get(url);
    return response.data;
  },

  sendCommunication: async (
    data: CommunicationPayload
  ): Promise<CommunicationPayload> => {
    const payload = {
      ...data,
    };
    const response = await communicationApiClient.post(
      ApiEndpoints.createUrl("communication", "direct-message"),
      payload
    );
    return response.data;
  },

  // Documents Tab Operations
  getDocuments: async (
    id: string,
    filters: DocumentFilters = {}
  ): Promise<PaginatedResponse<Document>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-document",
      (query) => {
        query.push("consumer_id", id);
        query.push("is_pagination_required", "True");
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.date_range?.start)
          query.push("start_date", filters.date_range.start);
        if (filters.date_range?.end)
          query.push("end_date", filters.date_range.end);
        if (filters.query) query.push("query", filters.query);

        if (filters.sort_field) query.push("sort_field", filters.sort_field);
        if (filters.sort_direction)
          query.push("sort_direction", filters.sort_direction);
        query.push("remote_utility_id", filters.remote_utility_id?.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<Document>
    >(response.data);
    return transformedResponse;
  },

  uploadDocument: async (
    id: string,
    remoteUtilityId: string,
    file: File,
    metadata: Partial<Document>
  ): Promise<Document> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("consumer_id", id);
    formData.append("remote_utility_id", remoteUtilityId?.toString());

    // Add metadata fields to form data
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    const response = await cxApiClient.post(
      ApiEndpoints.createUrlWithPathVariables("cx", "consumer-document", []),
      formData
    );
    return response.data;
  },

  getRelationshipInsights: async (
    id: string,
    remoteUtilityId?: string
  ): Promise<RelationshipInsights> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-overview",
      (params) => {
        params.push("consumer_id", id);
        if (remoteUtilityId) params.push("remote_utility_id", remoteUtilityId);
        return params;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data.result;
  },

  getConsumptionData: async (
    id: string,
    period = "6months",
    remoteUtilityId: any
  ): Promise<ConsumptionData> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "consumerWeb",
      "consumer-dashboard",
      ["graph"],
      (params) => {
        params.push("consumer_no", id); // Note: using consumer_no instead of consumer_id
        params.push("fetch_last_six_records", "True");
        params.push("utility_service", "Water");
        params.push("period", period);
        if (remoteUtilityId)
          params.push("remote_utility_id", remoteUtilityId.toString());
        return params;
      }
    );

    const response = await cxApiClient.get(url);
    return response.data.result;
  },

  getConsumerList: async (
    filters: ConsumerFilters = {}
  ): Promise<PaginatedResponse<Consumer>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (query) => {
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.search_data) query.push("search_data", filters.search_data);
        if (filters.category) query.push("category", filters.category);
        query.push("active_consumers", "1");
        // query.push('is_bill_data', '1');
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.status?.length) {
          filters.status.forEach((status) =>
            query.push("status", status.toString())
          );
        }
      }
    );

    const response = await cxApiClient.get(url);
    const transformedResponse = transformJsonToObject<
      PaginatedResponse<Consumer>
    >(response.data);
    return transformedResponse;
  },

  exportConsumerList: async (filters: ConsumerFilters = {}): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-add",
      (query) => {
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.search_data) query.push("search_data", filters.search_data);
        if (filters.category) query.push("category", filters.category);
        query.push("active_consumers", "1");
        // query.push('is_bill_data', '1');
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.status?.length) {
          filters.status.forEach((status) =>
            query.push("status", status.toString())
          );
        }
        query.push("export_format", "csv");
      }
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getConsumerRelation: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "choices/consumer_relation");
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPremise: async (params: {
    remote_utility_id: string;
    config_level: string;
  }): Promise<{ result: Premise[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", params.config_level);
      }
    );
    const response = await onboardingApiClient.get<{ result: Premise[] }>(url);
    return transformJsonToObject<{ result: Premise[] }>(response.data);
  },

  getAddressInfo: async (params: {
    remote_utility_id: string;
    premise: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "get-premise-info",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("premise", params.premise);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

searchPlan: async (params: {
  remoteUtilityId: string;
  searchField: string;
  searchData?: string;
}): Promise<{ result: Plan[] }> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "plan",
    (qs) => {
      qs.push("remote_utility_id", params.remoteUtilityId);

      // ✅ Only send if searchData is present
      if (params.searchData && params.searchData.trim() !== "") {
        qs.push("search_data", params.searchData);
      }
    }
  );

  const response = await onboardingApiClient.get<{ result: Plan[] }>(url);
  return transformJsonToObject<{ result: Plan[] }>(response.data);
},

  getPlan: async (params: {
    remoteUtilityId: string;
  }): Promise<{ result: Plan[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "plan",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId);
      }
    );
    const response = await onboardingApiClient.get<{ result: Plan[] }>(url);
    return transformJsonToObject<{ result: Plan[] }>(response.data);
  },

  createConsumer: async (
    payload: CreateConsumerPayload
  ): Promise<CreateConsumerResponse> => {
    const url = ApiEndpoints.createUrl("cx", "consumer");
    const response = await cxApiClient.post<CreateConsumerResponse>(
      url,
      payload
    );
    return transformJsonToObject<CreateConsumerResponse>(response.data);
  },

  updateConsumer: async (
    consumerId: string,
    payload: UpdateConsumerPayload
  ): Promise<UpdateConsumerResponse> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "consumer", [
      consumerId,
    ]);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  getPlanInfo: async (params: {
    consumer_id: string;
    remote_utility_id: string;
    is_plan_info: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-detail",
      (qs) => {
        qs.push("consumer_id", params.consumer_id);
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_plan_info", params.is_plan_info);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPaymentMode: async (params: {
    remote_utility_id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/payment_mode",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getEmailVerify: async (
    email: string,
    remoteUtilityId: number
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "verify",
      ["email"],
      (qs) => {
        qs.push("email", email);
        qs.push("remote_utility_id", remoteUtilityId);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getDetails: async (params: {
    consumer_id: string;
    remote_utility_id: string;
    is_detail_info: boolean;
    is_document: boolean;
  }): Promise<{ result: ConsumerDetailsResponse }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-detail",
      (qs) => {
        qs.push("consumer_id", params.consumer_id);
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_document", params.is_document);
      }
    );
    const response = await cxApiClient.get<{ result: ConsumerDetailsResponse }>(url);
    return transformJsonToObject<{ result: ConsumerDetailsResponse }>(response.data);
  },

  getAddress: async (params: {
    consumer_id: string;
    remote_utility_id: string;
    is_address_info: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-detail",
      (qs) => {
        qs.push("consumer_id", params.consumer_id);
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_address_info", params.is_address_info);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getKycInfo: async (params: {
    consumer_id: string;
    remote_utility_id: string;
    is_kyc_info: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "nsc-detail",
      (qs) => {
        qs.push("consumer_id", params.consumer_id);
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_kyc_info", params.is_kyc_info);
      }
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  editConsumer: async (consumerId: string, payload: any, type: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "consumer", [
      consumerId,
    ]);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },
  getDocumentType: async (params: {
    remote_utility_id: string;
    config_level: string;
  }): Promise<any> => {
    const response = await onboardingApiClient.get(
      `config-map/document_json/?remote_utility_id=${params.remote_utility_id}&config_level=${params.config_level}`
    );
    return response.data;
  },
  getDocumentSubtype: async (params: {
    remote_utility_id: string;
    config_level: string;
    code_list: string;
  }): Promise<any> => {
    const response = await onboardingApiClient.get(
      `config-map/document_json/?remote_utility_id=${
        params.remote_utility_id
      }&config_level=${params.config_level}&code_list=${encodeURIComponent(
        params.code_list
      )}`
    );
    return response.data;
  },
  getConsumerStatus: async (filters: {
    remote_utility_id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["consumer_status"],
      (qs) => {
        qs.push("remote_utility_id", filters.remote_utility_id?.toString());
      }
    );
    const response = await cxApiClient.get(url);
    return response.data.result;
  },
  getConsumerSummary: async (params: {
    remote_utility_id: number;
  }): Promise<ConsumerSummary> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-summary",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id.toString());
        qs.push("active_tab", "Active_Consumers");
      }
    );
    const response = await cxApiClient.get(url);
    return response.data.result;
  },
  getChannelType: async (): Promise<DropdownItem[]> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "communication",
      "choices",
      ["channel_type"]
    );
    const response = await communicationApiClient.get(url);
    return response.data.result;
  },
  getChannelTemplates: async (
    remoteUtilityId: any,
    channelType: string,
  ): Promise<DropdownItem[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "template-dropdown",
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId.toString());
        qs.push("channel_type", channelType);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data.result;
  },
  downloadBill: async (
    billId: string,
    remoteUtilityId: number
  ): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "bx",
      `render-dynamic-template`,
      [billId],
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId.toString());
      }
    );
    const response = await bxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },
  downloadPaymentReceipt: async (
    paymentId: string,
    remoteUtilityId: number
  ): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      `receipt-download`,
      [paymentId],
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId.toString());
        qs.push("receipt_type", "payment");
      }
    );
    const response = await cxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },

  getCategories: async (
    remoteUtilityId: string
  ): Promise<{ result: CategoryOption[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["category_json"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "category");
      }
    );
    const response = await onboardingApiClient.get<{
      result: CategoryOption[];
    }>(url);
    return transformJsonToObject<{ result: CategoryOption[] }>(response.data);
  },

  getSubcategories: async (
    remoteUtilityId: string,
    categoryCode: string
  ): Promise<{ result: SubcategoryOption[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["category_json"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "sub_category");
        query.push("code_list", categoryCode);
      }
    );
    const response = await onboardingApiClient.get<{
      result: SubcategoryOption[];
    }>(url);
    return transformJsonToObject<{ result: SubcategoryOption[] }>(
      response.data
    );
  },
  uploadDocumentsForConsumer: async (consumerId: string, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "consumer", [consumerId]);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  getPaymentChannelChoices: async (remoteUtilityId: number): Promise<{ result: PaymentChannel[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables('onboarding', 'config-map',['payment_channel'], (query) => {
      query.push('remote_utility_id', remoteUtilityId.toString());
      return query;
    });
    const response = await cxApiClient.get<{ result: PaymentChannel[] }>(url);
    return transformJsonToObject<{ result: PaymentChannel[] }>(response.data);
  },

  getPaymentPayTypeChoices: async (remoteUtilityId: number): Promise<{ result: PaymentPayType[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables('cx', 'choices',['payment_pay_type'], (query) => {
      query.push('remote_utility_id', remoteUtilityId.toString());
      return query;
    });
    const response = await cxApiClient.get<{ result: PaymentPayType[] }>(url);
    return transformJsonToObject<{ result: PaymentPayType[] }>(response.data);
  },


  getPaymentMethod: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: PaymentMethod[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables('onboarding', 'config-map',['payment_mode'], (query) => {
      query.push('remote_utility_id', params.remoteUtilityId.toString());
      return query;
    });
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<{ result: PaymentMethod[] }>(response.data);
  },

    // Create Payment
  createPayment: async (payload: CreatePaymentPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl('cx', 'payment');
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },


  getPlanDetails: async (params: {
    remoteUtilityId: string;
    planId: string;
  }): Promise<{ result: Plan }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "plan",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId);
        qs.push("remote_plan_id", params.planId);
      }
    );
    const response = await onboardingApiClient.get<{ result: Plan }>(url);
    return transformJsonToObject<{ result: Plan }>(response.data);
  },
};
